import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogoIcon } from "../components/Brand";
import { useApp } from "../AppContext";
import { motion } from "framer-motion";
import { ArrowRight, Eye, Sun, Moon } from "lucide-react";
import { useTheme } from "../hooks/useTheme";
import ProgereLegalFooter from "../components/ProgereLegalFooter";
import SupportBubble from "../components/SupportBubble";

const DEMO_CREDS = { email: "demo@progere.ca", password: "Demo2026!" };

export default function Auth({ mode = "login" }) {
  const { t, lang, setLang, login, verify2FA, register } = useApp();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const planParam = params.get("plan");
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "PROGÈRE Construction" });
  const [acceptLegal, setAcceptLegal] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [twoFa, setTwoFa] = useState(null); // { challenge_id, email_hint }
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const isFr = lang === "fr";

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(form.email, form.password);
        if (res?.requires_2fa) {
          setTwoFa({ challenge_id: res.challenge_id, email_hint: res.email_hint });
          setLoading(false);
          return;
        }
      } else {
        if (!acceptLegal) { setLoading(false); return setErr(isFr ? "Vous devez accepter les conditions et la politique de confidentialité" : "You must accept the terms and privacy policy"); }
        await register({ ...form, accept_terms: true, accept_privacy: true });
      }
      nav(planParam ? `/app/billing?plan=${planParam}` : "/app");
    } catch (e) {
      setErr(e?.response?.data?.detail || (isFr ? "Erreur" : "Error"));
    } finally { setLoading(false); }
  };

  const onVerify2FA = async (e) => {
    e.preventDefault();
    setErr(""); setOtpLoading(true);
    try {
      await verify2FA(twoFa.challenge_id, otpCode.trim());
      nav(planParam ? `/app/billing?plan=${planParam}` : "/app");
    } catch (e) {
      setErr(e?.response?.data?.detail || (isFr ? "Code invalide" : "Invalid code"));
    } finally { setOtpLoading(false); }
  };

  const demoLogin = async () => {
    setErr(""); setDemoLoading(true);
    try {
      await login(DEMO_CREDS.email, DEMO_CREDS.password);
      nav("/app");
    } catch (e) {
      setErr(isFr ? "Le compte démo n'est pas disponible pour le moment." : "Demo account is unavailable right now.");
    } finally { setDemoLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col relative"
      style={{ background: "radial-gradient(ellipse at top, rgba(212,175,55,0.08) 0%, transparent 55%), radial-gradient(ellipse at bottom, rgba(192,0,0,0.08) 0%, transparent 55%), #0B0B0B" }}>

      {/* Top-right controls */}
      <div className="absolute top-5 right-5 sm:top-6 sm:right-6 flex items-center gap-2 z-10">
        <button onClick={() => setLang(isFr ? "en" : "fr")}
          className="text-xs px-3 py-1.5 rounded-full transition hover:brightness-110"
          style={{ background: "rgba(245,245,245,0.06)", border: "1px solid rgba(245,245,245,0.1)", color: "rgba(245,245,245,0.85)" }}
          data-testid="auth-lang-toggle">
          {isFr ? "EN" : "FR"}
        </button>
        <button onClick={toggleTheme}
          className="w-9 h-9 rounded-full flex items-center justify-center transition hover:brightness-110"
          style={{ background: "rgba(245,245,245,0.06)", border: "1px solid rgba(245,245,245,0.1)" }}
          aria-label="Toggle theme" data-testid="auth-theme-toggle">
          {theme === "dark" ? <Sun size={14} color="#D4AF37" /> : <Moon size={14} color="#0B0B0B" />}
        </button>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex-1 w-full flex items-center justify-center px-4 sm:px-6 py-10">
        <div
        className="w-full max-w-md rounded-3xl p-8 sm:p-10 relative"
        style={{
          background: "linear-gradient(180deg, rgba(24,24,24,0.9) 0%, rgba(11,11,11,0.95) 100%)",
          border: "1px solid rgba(212,175,55,0.18)",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.03)",
        }}>
        {/* Hexagonal icon + PROGÈRE DEVIS AI heading (identical to PROGÈRE CRM) */}
        <div className="flex flex-col items-center mb-6">
          <div className="mb-4">
            <LogoIcon size="lg" />
          </div>
          <div className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-center leading-none">
            <span style={{ color: "#D4AF37" }}>PRO</span><span style={{ color: "#EF4444" }}>GÈRE</span>
            <span className="ml-2" style={{ color: "#F5F5F5" }}>DEVIS AI</span>
          </div>
        </div>

        <p className="text-center text-sm mb-8" style={{ color: "rgba(245,245,245,0.6)" }}>
          {isFr
            ? "L'estimation intelligente pour la construction au Québec."
            : "Smart estimating for Quebec construction."}
        </p>

        {/* Form */}
        {twoFa ? (
          <form onSubmit={onVerify2FA} className="space-y-4" data-testid="auth-2fa-form">
            <div className="p-4 rounded-2xl mb-2" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)" }}>
              <div className="text-[10px] uppercase tracking-widest font-semibold mb-1" style={{ color: "#D4AF37" }}>
                {isFr ? "Double authentification" : "Two-factor authentication"}
              </div>
              <div className="text-sm" style={{ color: "rgba(245,245,245,0.85)" }}>
                {isFr ? "Un code de 6 chiffres a été envoyé à " : "A 6-digit code was sent to "}
                <span style={{ color: "#F5F5F5", fontWeight: 600 }}>{twoFa.email_hint}</span>
              </div>
            </div>
            <FieldLabel>{isFr ? "CODE À 6 CHIFFRES" : "6-DIGIT CODE"}</FieldLabel>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              className="input-dark text-center tracking-[0.6em] font-mono text-2xl"
              placeholder="••••••"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              required autoFocus data-testid="auth-2fa-code" />
            {err && (
              <div className="text-sm px-3 py-2.5 rounded-xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }}>
                {err}
              </div>
            )}
            <button type="submit" disabled={otpLoading || otpCode.length !== 6} className="btn-gold w-full mt-2 disabled:opacity-50" data-testid="auth-2fa-submit">
              {otpLoading ? "..." : (<><ArrowRight size={16} />{isFr ? "Vérifier et se connecter" : "Verify and sign in"}</>)}
            </button>
            <button type="button" onClick={() => { setTwoFa(null); setOtpCode(""); setErr(""); }} className="btn-ghost w-full justify-center" data-testid="auth-2fa-back">
              {isFr ? "← Retour" : "← Back"}
            </button>
          </form>
        ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {mode === "register" && (
            <>
              <FieldLabel>{isFr ? "NOM COMPLET" : "FULL NAME"}</FieldLabel>
              <input className="input-dark" placeholder={isFr ? "Jean Tremblay" : "John Smith"}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="auth-name" />
              <FieldLabel>{isFr ? "ENTREPRISE" : "COMPANY"}</FieldLabel>
              <input className="input-dark" placeholder={isFr ? "Votre entreprise inc." : "Your company inc."}
                value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="auth-company" />
            </>
          )}

          <FieldLabel>{isFr ? "COURRIEL" : "EMAIL"}</FieldLabel>
          <input type="email" className="input-dark" placeholder="vous@entreprise.ca"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="auth-email" />

          <div className="flex items-center justify-between">
            <FieldLabel>{isFr ? "MOT DE PASSE" : "PASSWORD"}</FieldLabel>
            {mode === "login" && (
              <Link to="/forgot-password" className="text-xs hover:underline" style={{ color: "#D4AF37" }} data-testid="auth-forgot">
                {isFr ? "Mot de passe oublié ?" : "Forgot password?"}
              </Link>
            )}
          </div>
          <input type="password" className="input-dark" placeholder="••••••••"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="auth-password" />

          {mode === "register" && (
            <label className="flex items-start gap-2 text-xs cursor-pointer pt-1" style={{ color: "rgba(245,245,245,0.65)" }} data-testid="legal-consent">
              <input type="checkbox" checked={acceptLegal} onChange={(e) => setAcceptLegal(e.target.checked)}
                className="accent-[#D4AF37] mt-0.5" required data-testid="legal-consent-checkbox" />
              <span>
                {isFr ? "J'accepte les " : "I accept the "}
                <Link to="/conditions" target="_blank" style={{ color: "#D4AF37" }}>{isFr ? "conditions d'utilisation" : "terms of service"}</Link>
                {isFr ? " et la " : " and the "}
                <Link to="/confidentialite" target="_blank" style={{ color: "#D4AF37" }}>{isFr ? "politique de confidentialité" : "privacy policy"}</Link> (Loi 25 / PIPEDA).
              </span>
            </label>
          )}

          {err && (
            <div className="text-sm px-3 py-2.5 rounded-xl"
              style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }}>
              {err}
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-gold w-full mt-2" data-testid="auth-submit">
            {loading ? "..." : (
              <>
                <ArrowRight size={16} />
                {mode === "login"
                  ? (isFr ? "Se connecter" : "Sign in")
                  : (isFr ? "Créer mon compte" : "Create account")}
              </>
            )}
          </button>
        </form>
        )}

        {/* Divider */}
        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px" style={{ background: "rgba(245,245,245,0.08)" }} />
          <span className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(245,245,245,0.35)" }}>
            {isFr ? "OU" : "OR"}
          </span>
          <div className="flex-1 h-px" style={{ background: "rgba(245,245,245,0.08)" }} />
        </div>

        {/* Demo login */}
        <button onClick={demoLogin} disabled={demoLoading} className="btn-ghost w-full justify-center" data-testid="auth-demo-btn">
          <Eye size={14} />
          {demoLoading
            ? (isFr ? "Chargement..." : "Loading...")
            : (isFr ? "Découvrir la démo en direct" : "Try the live demo")}
        </button>

        <p className="mt-4 text-center text-xs" style={{ color: "rgba(245,245,245,0.5)" }}>
          {isFr
            ? "Explorez PROGÈRE Devis AI avec des données de démonstration."
            : "Explore PROGÈRE Devis AI with demo data."}
        </p>

        {/* Register / Login switch */}
        <div className="mt-6 pt-4 text-center text-xs border-t" style={{ borderColor: "rgba(245,245,245,0.06)" }}>
          <div>
            <Link to={(mode === "login" ? "/register" : "/login") + (planParam ? `?plan=${planParam}` : "")}
              className="hover:underline" data-testid="auth-switch">
              <span style={{ color: "rgba(245,245,245,0.6)" }}>
                {mode === "login"
                  ? (isFr ? "Pas de compte ? " : "No account? ")
                  : (isFr ? "Déjà un compte ? " : "Already have an account? ")}
              </span>
              <span style={{ color: "#D4AF37", fontWeight: 600 }}>
                {mode === "login"
                  ? (isFr ? "Créer mon compte" : "Sign up")
                  : (isFr ? "Se connecter" : "Sign in")}
              </span>
            </Link>
          </div>
          <div className="mt-3">
            <Link to="/client-portal" className="hover:underline" data-testid="auth-client-portal">
              <span style={{ color: "rgba(245,245,245,0.55)" }}>
                {isFr ? "Vous êtes un client ? " : "Are you a client? "}
              </span>
              <span style={{ color: "#F5F5F5", fontWeight: 600 }}>
                {isFr ? "Accéder à l'espace client" : "Access client portal"} →
              </span>
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-[11px]" style={{ color: "rgba(245,245,245,0.4)" }}>
          PROGÈRE — {isFr ? "Propulsé par" : "Powered by"}{" "}
          <span style={{ color: "#D4AF37" }}>Groupe MJR Construction Inc.</span>
        </p>
      </div>
      </motion.div>

      <ProgereLegalFooter showLogin={false} />
      <SupportBubble />
    </div>
  );
}

const FieldLabel = ({ children }) => (
  <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: "rgba(245,245,245,0.7)" }}>
    {children}
  </label>
);
